import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from './supabaseClient';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import jsPDF from 'jspdf';
import { CLASE8_SUBCATEGORIAS } from './lib/sgaClasses';
import { nivelProductos } from './lib/compatibilidad';
import './CompatibilityMatrix.css';
import SelectBuscable from './components/SelectBuscable';
import { plural } from './lib/plurales';

// Bodega y zona con buscador (PE-C1): mismo aspecto que tenía .cm-wh-select.
const CM_WH_INPUT: React.CSSProperties = {
  padding: '9px 32px 9px 14px', borderRadius: 10, border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0',
  fontSize: '0.85rem', outline: 'none', transition: 'border-color 0.15s', width: '100%',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Product  {
  id: string;
  name: string;
  sga_classes: string[];
  reactive_hazards: string | null;
  incompatible_materials: string | null;
  clase8_subcategoria?: string | null;
}
interface Warehouse { id: string; name: string; description: string | null; }
interface Zone     { id: string; warehouse_id: string; name: string; product_ids: string[]; }
type CompatLevel = 'compatible' | 'precaucion' | 'incompatible' | 'self';
type TabId       = 'general' | 'comparar' | 'bodega';

// NOTA (2026-09-16): Kevin pidió agregar "Ácidos Fuertes" como advertencia visual
// en la Matriz de Compatibilidad. Se optó por NO crear una subcategoría nueva
// llamada "acido_fuerte" porque ya existían 3 categorías de ácido en
// CLASE8_SUBCATEGORIAS (acido_oxidante, acido_no_oxidante, acido_organico) que
// cubrían el concepto, y crear una 4ta se superpondría/confundiría con esas.
// Kevin luego generalizó el pedido a "con cualquier subcategoría de Clase 8" —
// por eso este warning es genérico sobre las 8 categorías existentes, no un
// caso especial de ácidos fuertes. El cálculo de compatibilidad NO se modifica,
// esto es únicamente informativo/visual.
function getClase8Label(value: string): string {
  return CLASE8_SUBCATEGORIAS.find(c => c.value === value)?.label ?? value;
}

// ── Compatibilidad: tabla única (src/lib/compatibilidad.ts) ──────────────────

function productCompat(a: Product, b: Product): CompatLevel {
  if (a.id === b.id) return 'self';
  return nivelProductos(a, b);
}

function calcSummary(prods: Product[]) {
  let ok = 0, warn = 0, bad = 0;
  for (let i = 0; i < prods.length; i++)
    for (let j = i + 1; j < prods.length; j++) {
      const l = productCompat(prods[i], prods[j]);
      if (l === 'incompatible') bad++;
      else if (l === 'precaucion') warn++;
      else ok++;
    }
  return { ok, warn, bad };
}

// ── Hazard data (INEN 2266) ───────────────────────────────────────────────────

const HAZARD_RGB: Record<string, [number, number, number]> = {
  'Explosivo':         [239, 68,  68],
  'Inflamable':        [249, 115, 22],
  'Genera gas':        [59,  130, 246],
  'Genera calor':      [245, 158, 11],
  'Reaccion intensa':  [139, 92,  246],
  'Toxico':            [132, 204, 22],
};

interface ClassPairData { hazards: string[]; gases: string[]; note: string; }

const CLASS_PAIR_DATA: Record<string, ClassPairData> = {
  'clase1-clase3':       { hazards: ['Explosivo','Inflamable','Reaccion intensa'], gases: [],                               note: 'El liquido inflamable puede activar el explosivo y provocar detonacion.' },
  'clase1-clase4-1':     { hazards: ['Explosivo','Inflamable'],                    gases: [],                               note: 'Riesgo de detonacion o incendio intenso.' },
  'clase1-clase4-2':     { hazards: ['Explosivo','Inflamable'],                    gases: [],                               note: 'Riesgo de detonacion o incendio intenso.' },
  'clase1-clase4-3':     { hazards: ['Explosivo','Inflamable'],                    gases: [],                               note: 'Riesgo de detonacion o incendio intenso.' },
  'clase1-clase5-1':     { hazards: ['Explosivo','Reaccion intensa'],              gases: [],                               note: 'Los oxidantes pueden desestabilizar el explosivo y causar detonacion.' },
  'clase1-clase5-2':     { hazards: ['Explosivo','Reaccion intensa'],              gases: [],                               note: 'Los peroxidos organicos pueden desestabilizar el explosivo y causar detonacion.' },
  'clase1-clase8':       { hazards: ['Explosivo','Genera calor'],                  gases: [],                               note: 'Los corrosivos pueden iniciar reaccion con el material explosivo.' },
  'clase3-clase5-1':     { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','Vapores organicos'],      note: 'Los oxidantes pueden causar ignicion espontanea de liquidos inflamables.' },
  'clase3-clase5-2':     { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','Vapores organicos'],      note: 'Los peroxidos organicos pueden causar ignicion espontanea de liquidos inflamables.' },
  'clase3-clase8':       { hazards: ['Inflamable','Genera calor','Toxico'],        gases: ['HCl','SO2','Vapores organicos'], note: 'Reaccion exotermica con emision de vapores toxicos y corrosivos.' },
  'clase4-1-clase5-1':   { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','SO2'],                    note: 'Ignicion espontanea posible. Riesgo de incendio severo.' },
  'clase4-1-clase5-2':   { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','SO2'],                    note: 'Ignicion espontanea posible. Riesgo de incendio severo.' },
  'clase4-2-clase5-1':   { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','SO2'],                    note: 'Sustancias piroforicas con oxidantes: riesgo extremo de incendio.' },
  'clase4-2-clase5-2':   { hazards: ['Inflamable','Reaccion intensa'],             gases: ['CO2','SO2'],                    note: 'Sustancias piroforicas con peroxidos organicos: riesgo extremo de incendio.' },
  'clase4-3-clase5-1':   { hazards: ['Inflamable','Genera gas','Reaccion intensa'],gases: ['H2','SO2'],                     note: 'En contacto con agua el gas inflamable generado puede ser oxidado violentamente.' },
  'clase4-3-clase5-2':   { hazards: ['Inflamable','Genera gas','Reaccion intensa'],gases: ['H2','SO2'],                     note: 'Peroxidos organicos pueden iniciar reaccion con gases inflamables del agua.' },
  'clase5-1-clase8':     { hazards: ['Genera gas','Reaccion intensa','Toxico'],    gases: ['Cl2','O2','NO2','HF'],          note: 'Reaccion violenta con liberacion de gases toxicos y oxidantes.' },
  'clase5-2-clase8':     { hazards: ['Genera gas','Reaccion intensa','Toxico'],    gases: ['Cl2','O2','NO2'],               note: 'Los peroxidos organicos reaccionan violentamente con corrosivos.' },
  'clase2-1-clase3':     { hazards: ['Inflamable'],                                gases: [],                               note: 'Los gases inflamables pueden incrementar el riesgo de ignicion de liquidos inflamables.' },
  'clase2-2-clase3':     { hazards: ['Inflamable'],                                gases: [],                               note: 'Gases a presion pueden dispersar liquidos inflamables aumentando el riesgo de ignicion.' },
  'clase2-3-clase3':     { hazards: ['Inflamable','Toxico'],                       gases: ['Vapores toxicos'],              note: 'Gases toxicos combinados con vapores de liquidos inflamables: doble riesgo.' },
  'clase3-clase6':       { hazards: ['Inflamable','Toxico'],                       gases: ['Vapores toxicos'],              note: 'Vapores inflamables con toxicos aumentan el riesgo de exposicion.' },
  'clase6-clase8':       { hazards: ['Toxico','Genera gas'],                       gases: ['Vapores toxicos','HCl','NH3'],  note: 'Los corrosivos pueden volatilizar sustancias toxicas e infecciosas.' },
};

function getPairDetail(a: Product, b: Product): { hazards: string[]; gases: string[]; note: string } {
  const hazards = new Set<string>();
  const gases   = new Set<string>();
  let note = '';
  for (const ca of a.sga_classes) {
    for (const cb of b.sga_classes) {
      const key = [ca, cb].sort().join('-');
      const d = CLASS_PAIR_DATA[key];
      if (d) {
        d.hazards.forEach(h => hazards.add(h));
        d.gases.forEach(g => gases.add(g));
        if (!note) note = d.note;
      }
    }
  }
  return { hazards: Array.from(hazards), gases: Array.from(gases), note };
}

// ── PDF export (pure jsPDF, sin html2canvas) ──────────────────────────────────

async function exportToPDF(
  prods: Product[],
  subtitle: string,
  setExporting: (v: boolean) => void,
  companyName?: string | null,
  companyRuc?: string | null,
) {
  if (prods.length < 2) return;
  setExporting(true);

  try {
    const pdf  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw   = pdf.internal.pageSize.getWidth();   // 210
    const ph   = pdf.internal.pageSize.getHeight();  // 297
    const M    = 14;
    const UW   = pw - 2 * M;   // 182
    const FTR  = 19;            // footer height reserved
    let   y    = 0;

    // ── helpers ────────────────────────────────────────────────────────────────

    const fill  = (r: number, g: number, b: number) => pdf.setFillColor(r, g, b);
    const stroke = (r: number, g: number, b: number) => pdf.setDrawColor(r, g, b);
    const color = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);
    const font  = (style: 'normal'|'bold'|'italic', size: number) => {
      pdf.setFont('helvetica', style);
      pdf.setFontSize(size);
    };
    const rect  = (x: number, ry: number, w: number, h: number) => pdf.rect(x, ry, w, h, 'F');
    const rrect = (x: number, ry: number, w: number, h: number, r: number) => pdf.roundedRect(x, ry, w, h, r, r, 'F');
    const txt   = (s: string, x: number, ty: number, opts?: object) => pdf.text(s, x, ty, opts ?? {});
    const line  = (x1: number, y1: number, x2: number, y2: number) => pdf.line(x1, y1, x2, y2);

    // Mini-header (membrete) for continuation pages
    const contHeader = () => {
      fill(15, 23, 42); rect(0, 0, pw, 15);
      fill(34, 197, 94); rect(0, 14, pw, 1);
      font('bold', 7); color(34, 197, 94);
      txt('StorSafe', M, 7);
      font('normal', 7); color(100, 116, 139);
      txt('· Predicciones de Reactividad · NTE INEN 2266:2013', M + 14, 7);
      const d = new Date().toLocaleDateString('es-EC', { day: 'numeric', month: 'short', year: 'numeric' });
      txt(d, pw - M, 7, { align: 'right' });
      font('normal', 6.5); color(148, 163, 184);
      const empresaLine = companyRuc ? `${companyName ?? ''} · RUC: ${companyRuc}` : (companyName ?? '');
      if (empresaLine) txt(empresaLine, M, 12);
    };

    const checkPage = (needed: number) => {
      if (y + needed > ph - FTR) {
        pdf.addPage();
        contHeader();
        y = 21;
      }
    };

    // Section title bar
    const section = (num: string, title: string, ar: number, ag: number, ab: number) => {
      checkPage(14);
      fill(22, 33, 51); rect(M, y, UW, 9);
      fill(ar, ag, ab); rect(M, y, 3.5, 9);
      font('bold', 8); color(226, 232, 240);
      txt(`${num}  ${title}`, M + 7, y + 6.3);
      y += 9 + 5;
    };

    // ── PAGE 1 MAIN HEADER ────────────────────────────────────────────────────

    const HDR = 44;
    fill(15, 23, 42); rect(0, 0, pw, HDR);
    fill(34, 197, 94); rect(0, HDR - 2, pw, 2);

    // Logo
    fill(34, 197, 94); rrect(M, 10, 22, 22, 3);
    font('bold', 12); color(255, 255, 255);
    txt('SS', M + 11, 23.5, { align: 'center' });

    // Main title
    font('bold', 19); color(255, 255, 255);
    txt('PREDICCIONES DE REACTIVIDAD', M + 28, 20);

    font('normal', 9); color(148, 163, 184);
    txt('Analisis de compatibilidad quimica · NTE INEN 2266:2013', M + 28, 27);

    if (subtitle) {
      font('normal', 8); color(100, 116, 139);
      txt(subtitle, M + 28, 33);
    }

    const empresaLine = companyRuc ? `${companyName ?? ''} · RUC: ${companyRuc}` : (companyName ?? '');
    if (empresaLine) {
      font('normal', 7.5); color(148, 163, 184);
      txt(empresaLine, M + 28, 39.5);
    }

    const fecha = new Date().toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric' });
    font('normal', 8); color(100, 116, 139);
    txt(`Generado: ${fecha}`, pw - M, 18, { align: 'right' });
    txt(`${prods.length} productos analizados`, pw - M, 25, { align: 'right' });

    y = HDR + 9;

    // ── SECTION 1 — VISUAL MATRIX ─────────────────────────────────────────────

    section('1.', 'MATRIZ DE COMPATIBILIDAD', 34, 197, 94);

    const N      = prods.length;
    const NUM_W  = 8;
    const CELL_S = Math.max(6, Math.min(14, Math.floor((UW - NUM_W) / N)));
    const CELL_H = 9;
    const HDR_H  = 9;

    const matH = HDR_H + N * CELL_H;
    checkPage(matH + 6);

    const mx  = M;
    const myt = y;

    // Corner
    fill(15, 23, 42); rect(mx, myt, NUM_W, HDR_H);
    font('normal', 5.5); color(71, 85, 105);
    txt('#', mx + NUM_W / 2, myt + 6.2, { align: 'center' });

    // Column headers (numbers)
    for (let ci = 0; ci < N; ci++) {
      const cx = mx + NUM_W + ci * CELL_S;
      fill(15, 23, 42); rect(cx, myt, CELL_S, HDR_H);
      stroke(22, 33, 51); pdf.setLineWidth(0.2); pdf.rect(cx, myt, CELL_S, HDR_H, 'S');
      font('bold', 7); color(148, 163, 184);
      txt(String(ci + 1), cx + CELL_S / 2, myt + 6.5, { align: 'center' });
    }

    // Rows
    const cellCfg: Record<CompatLevel, { bg: [number,number,number]; fg: [number,number,number]; icon: string }> = {
      compatible:   { bg: [20,83,45],   fg: [74,222,128],  icon: '✓' },
      precaucion:   { bg: [113,63,18],  fg: [251,191,36],  icon: '!' },
      incompatible: { bg: [127,29,29],  fg: [252,165,165], icon: '✕' },
      self:         { bg: [15,23,42],   fg: [51,65,85],    icon: '—' },
    };

    for (let ri = 0; ri < N; ri++) {
      const ry = myt + HDR_H + ri * CELL_H;
      fill(22, 33, 51); rect(mx, ry, NUM_W, CELL_H);
      stroke(15, 23, 42); pdf.setLineWidth(0.2); pdf.rect(mx, ry, NUM_W, CELL_H, 'S');
      font('bold', 7); color(148, 163, 184);
      txt(String(ri + 1), mx + NUM_W / 2, ry + CELL_H / 2 + 2.5, { align: 'center' });

      for (let ci = 0; ci < N; ci++) {
        const cx  = mx + NUM_W + ci * CELL_S;
        const lv  = productCompat(prods[ri], prods[ci]);
        const cfg = cellCfg[lv];
        const [br, bg, bb] = cfg.bg;
        const [fr, fg, fb] = cfg.fg;
        fill(br, bg, bb); rect(cx, ry, CELL_S, CELL_H);
        stroke(15, 23, 42); pdf.setLineWidth(0.2); pdf.rect(cx, ry, CELL_S, CELL_H, 'S');
        font('bold', 8); color(fr, fg, fb);
        txt(cfg.icon, cx + CELL_S / 2, ry + CELL_H / 2 + 2.8, { align: 'center' });
      }
    }

    y = myt + matH + 5;

    // Product index (2 columns)
    const idxRows = Math.ceil(N / 2);
    checkPage(idxRows * 5 + 8);

    font('bold', 6.5); color(71, 85, 105);
    txt('Indice de productos:', M, y); y += 4;

    prods.forEach((p, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const ix  = M + col * (UW / 2);
      const iy  = y + row * 5;
      font('normal', 7); color(148, 163, 184);
      const label = `${idx + 1}.  ${p.name}`;
      txt(label.length > 36 ? label.slice(0, 35) + '…' : label, ix, iy);
    });

    y += idxRows * 5 + 8;

    // ── SECTION 2 — LEGEND ───────────────────────────────────────────────────

    checkPage(34);
    section('2.', 'LEYENDA', 239, 68, 68);

    const legItems = [
      { icon: '✕', label: 'Incompatible', desc: 'No almacenar juntos — riesgo grave', r: 239, g: 68,  b_: 68  },
      { icon: '!',  label: 'Precaucion',   desc: 'Separar fisicamente segun la SDS',    r: 234, g: 179, b_: 8   },
      { icon: '✓',  label: 'Compatible',   desc: 'Sin restricciones especiales',        r: 34,  g: 197, b_: 94  },
    ];
    const legW = (UW - 8) / 3;

    legItems.forEach(({ icon, label, desc, r, g, b_ }, idx) => {
      const lx = M + idx * (legW + 4);
      fill(22, 33, 51); rrect(lx, y, legW, 14, 2);
      fill(r, g, b_);   rrect(lx, y, 3, 14, 2); rect(lx + 1.5, y, 1.5, 14);
      fill(r, g, b_); pdf.circle(lx + 10, y + 7, 3.5, 'F');
      font('bold', 8); color(255, 255, 255);
      txt(icon, lx + 10, y + 9, { align: 'center' });
      font('bold', 8.5); color(r, g, b_);
      txt(label, lx + 17, y + 6);
      font('normal', 6.5); color(100, 116, 139);
      txt(desc, lx + 17, y + 11, { maxWidth: legW - 18 });
    });

    y += 14 + 9;

    // ── COLLECT PAIRS ─────────────────────────────────────────────────────────

    interface PairEntry { a: Product; b: Product; level: CompatLevel; hazards: string[]; gases: string[]; note: string; }

    const nonCompatPairs: PairEntry[] = [];
    for (let i = 0; i < prods.length; i++)
      for (let j = i + 1; j < prods.length; j++) {
        const level = productCompat(prods[i], prods[j]);
        if (level === 'compatible' || level === 'self') continue;
        nonCompatPairs.push({ a: prods[i], b: prods[j], level, ...getPairDetail(prods[i], prods[j]) });
      }

    nonCompatPairs.sort((x, z) => (x.level === 'incompatible' ? 0 : 1) - (z.level === 'incompatible' ? 0 : 1));

    if (nonCompatPairs.length > 0) {

      // ── SECTION 3 — HAZARD SUMMARY ──────────────────────────────────────────

      const allHazardTypes = Array.from(new Set(nonCompatPairs.flatMap(p => p.hazards)));
      const hRows = Math.ceil(allHazardTypes.length / 4);
      const hBW   = (UW - 4 * 3) / 4;
      const hBH   = 9;

      checkPage(14 + hRows * (hBH + 3) + 6);
      section('3.', 'RESUMEN DE PELIGROS DETECTADOS', 245, 158, 11);

      allHazardTypes.forEach((hazard, idx) => {
        const col = idx % 4;
        const row = Math.floor(idx / 4);
        const hx  = M + col * (hBW + 4);
        const hy  = y + row * (hBH + 3);
        const [hr, hg, hb] = HAZARD_RGB[hazard] ?? [100, 116, 139];
        fill(22, 33, 51); rrect(hx, hy, hBW, hBH, 1.5);
        fill(hr, hg, hb); rrect(hx, hy, 2.5, hBH, 1.5); rect(hx + 1.5, hy, 1, hBH);
        fill(hr, hg, hb); pdf.circle(hx + 7, hy + hBH / 2, 2, 'F');
        font('bold', 7.5); color(hr, hg, hb);
        txt(hazard, hx + 13, hy + 6);
      });

      y += hRows * (hBH + 3) + 9;

      // ── SECTION 4 — PAIR DETAIL CARDS ───────────────────────────────────────

      section('4.', 'ANALISIS DETALLADO POR PAR', 139, 92, 246);

      for (const { a, b, level, hazards, gases, note } of nonCompatPairs) {
        const isInc = level === 'incompatible';
        const [br, bg, bb] = isInc ? [239,68,68] : [234,179,8];
        const statusLabel   = isInc ? 'INCOMPATIBLE' : 'SEGREGAR';

        const hRows2 = Math.ceil(hazards.length / 3);
        const CARD_H =
          8 +           // top padding + product names
          6 +           // rule + status row
          (hazards.length > 0 ? 5 + hRows2 * 6 + 2 : 0) +
          (gases.length  > 0  ? 7 : 0) +
          (note           ? Math.ceil(note.length / 70) * 4 + 4 : 0) +
          5;            // bottom padding

        checkPage(CARD_H + 5);

        const cx = M;
        const cy = y;

        // Card background
        fill(18, 26, 40); rrect(cx, cy, UW, CARD_H, 2.5);
        // Left border
        fill(br, bg, bb); rrect(cx, cy, 4, CARD_H, 2.5); rect(cx + 2, cy, 2, CARD_H);
        // Top tint strip
        fill(Math.round(br * 0.2), Math.round(bg * 0.2), Math.round(bb * 0.2));
        rect(cx + 4, cy, UW - 4, 1.5);

        const IX  = cx + 9;
        const IW  = UW - 14;
        let   cy2 = cy + 8;

        // Product names
        const nameStr = `${a.name}  +  ${b.name}`;
        font('bold', 9); color(226, 232, 240);
        txt(nameStr.length > 72 ? nameStr.slice(0, 71) + '…' : nameStr, IX, cy2);

        // Status badge (top-right)
        const bBW = 30;
        const bBX = cx + UW - bBW - 4;
        fill(br, bg, bb); rrect(bBX, cy + 4, bBW, 8, 1.5);
        font('bold', 6.5); color(255, 255, 255);
        txt(statusLabel, bBX + bBW / 2, cy + 9.2, { align: 'center' });

        cy2 += 6;

        // Rule
        stroke(30, 41, 59); pdf.setLineWidth(0.3);
        line(IX, cy2, cx + UW - 4, cy2); cy2 += 4;

        // Hazards
        if (hazards.length > 0) {
          font('bold', 6.5); color(71, 85, 105);
          txt('PELIGROS:', IX, cy2); cy2 += 5;

          hazards.forEach((h, hi) => {
            const hcol = hi % 3;
            const hrow = Math.floor(hi / 3);
            const hx   = IX + hcol * (IW / 3);
            const hy   = cy2 + hrow * 6;
            const [hr, hg, hb] = HAZARD_RGB[h] ?? [100, 116, 139];
            fill(hr, hg, hb); pdf.circle(hx + 1.5, hy - 1.2, 1.5, 'F');
            font('bold', 7); color(hr, hg, hb);
            txt(h, hx + 5, hy);
          });
          cy2 += hRows2 * 6 + 2;
        }

        // Gases
        if (gases.length > 0) {
          font('bold', 6.5); color(71, 85, 105);
          txt('GASES:', IX, cy2);
          font('normal', 7); color(148, 163, 184);
          txt(gases.join('  ·  '), IX + 12, cy2); cy2 += 7;
        }

        // Note
        if (note) {
          font('italic', 6.5); color(71, 85, 105);
          txt(note, IX, cy2, { maxWidth: IW });
        }

        y = cy + CARD_H + 5;
      }
    }

    // ── FOOTER on all pages ────────────────────────────────────────────────────

    const total = pdf.getNumberOfPages();
    for (let pg = 1; pg <= total; pg++) {
      pdf.setPage(pg);
      fill(15, 23, 42); rect(0, ph - FTR, pw, FTR);
      fill(34, 197, 94); rect(0, ph - FTR, pw, 0.5);
      font('italic', 5.5); color(71, 85, 105);
      txt('AVISO LEGAL: StorSafe es una herramienta de apoyo. La informacion es responsabilidad exclusiva del usuario.', M, ph - 12.5);
      txt('No reemplaza asesoria profesional en seguridad quimica. StorSafe no asume responsabilidad por danos directos, indirectos o lucro cesante.', M, ph - 8);
      font('normal', 7); color(71, 85, 105);
      txt('Basado en la NTE INEN 2266:2013  ·  StorSafe — Gestion Segura de Bodegas Quimicas', M, ph - 3.5);
      txt(`Pag. ${pg} / ${total}`, pw - M, ph - 3.5, { align: 'right' });
    }

    pdf.save(`storsafe-reactividad-${Date.now()}.pdf`);

  } catch (err) {
    console.error('PDF export error:', err);
  }
  setExporting(false);
}

// ── Legend config ─────────────────────────────────────────────────────────────

const LEGEND = [
  { level: 'compatible'   as CompatLevel, label: 'Compatible',   desc: 'Pueden almacenarse juntos',                                 color: '#22c55e', bg: 'rgba(34,197,94,0.15)'  },
  { level: 'precaucion'   as CompatLevel, label: 'Precaución',   desc: 'Revisar incompatibilidades individuales',                  color: '#eab308', bg: 'rgba(234,179,8,0.15)'  },
  { level: 'incompatible' as CompatLevel, label: 'Incompatible', desc: 'Almacenamiento separado obligatorio',                      color: '#ef4444', bg: 'rgba(239,68,68,0.15)'  },
];

// ── Cell ──────────────────────────────────────────────────────────────────────

function Cell({ level, rowProduct, colProduct, colIndex, rowId, colId, onHover, onShowTooltip, isRowHighlighted, isColHighlighted }: {
  level: CompatLevel;
  rowProduct?: Product;
  colProduct?: Product;
  colIndex?: number;
  rowId?: string;
  colId?: string;
  onHover?: (row: string | null, col: string | null) => void;
  onShowTooltip?: (data: { rowProduct: Product; colProduct: Product; rect: DOMRect } | null) => void;
  isRowHighlighted?: boolean;
  isColHighlighted?: boolean;
}) {
  if (level === 'self')
    return <td className="cm-cell cm-cell-self"><span className="cm-self-mark">—</span></td>;

  const map = {
    compatible:   { cls: 'cm-cell-ok',   icon: '✓' },
    precaucion:   { cls: 'cm-cell-warn', icon: '!' },
    incompatible: { cls: 'cm-cell-bad',  icon: '✕' },
  } as const;
  const { cls, icon } = map[level];

  const highlighted = isRowHighlighted || isColHighlighted;
  const showTooltip = (level === 'precaucion' || level === 'incompatible') && rowProduct && colProduct;

  if (!showTooltip)
    return (
      <td
        className={`cm-cell ${cls}${highlighted ? ' cm-cell-highlighted' : ''}`}
        onMouseEnter={() => onHover?.(rowId ?? null, colId ?? null)}
        onMouseLeave={() => onHover?.(null, null)}
      >
        <span className="cm-icon">{icon}</span>
      </td>
    );

  const hasInfo = rowProduct.reactive_hazards || rowProduct.incompatible_materials ||
                  colProduct.reactive_hazards || colProduct.incompatible_materials;

  return (
    <td
      className={`cm-cell ${cls} cm-cell-has-tooltip${highlighted ? ' cm-cell-highlighted' : ''}`}
      onMouseEnter={(event: React.MouseEvent<HTMLTableCellElement>) => {
        onHover?.(rowId ?? null, colId ?? null);
        if (hasInfo) onShowTooltip?.({ rowProduct, colProduct, rect: event.currentTarget.getBoundingClientRect() });
      }}
      onMouseLeave={() => {
        onHover?.(null, null);
        onShowTooltip?.(null);
      }}
    >
      <span className="cm-icon">{icon}</span>
    </td>
  );
}

// ── MatrixPanel (reusable) ────────────────────────────────────────────────────

function MatrixPanel({ products }: { products: Product[] }) {
  const { ok, warn, bad } = calcSummary(products);
  const [hoveredCell, setHoveredCell] = useState<{ row: string; col: string } | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    rowProduct: Product;
    colProduct: Product;
    rect: DOMRect;
  } | null>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const tooltipCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelTooltipClose = useCallback(() => {
    if (tooltipCloseTimer.current) {
      clearTimeout(tooltipCloseTimer.current);
      tooltipCloseTimer.current = null;
    }
  }, []);

  const scheduleTooltipClose = useCallback(() => {
    cancelTooltipClose();
    tooltipCloseTimer.current = setTimeout(() => setTooltipData(null), 120);
  }, [cancelTooltipClose]);

  const showTooltipNow = useCallback((data: { rowProduct: Product; colProduct: Product; rect: DOMRect } | null) => {
    if (data === null) {
      scheduleTooltipClose();
      return;
    }
    cancelTooltipClose();
    setTooltipData(data);
  }, [cancelTooltipClose, scheduleTooltipClose]);

  useEffect(() => () => cancelTooltipClose(), [cancelTooltipClose]);
  return (
    <>
      <div className="cm-summary">
        <div className="cm-summary-card cm-summary-ok">
          <span className="cm-summary-count">{ok}</span>
          <span className="cm-summary-label">Compatibles</span>
        </div>
        <div className="cm-summary-card cm-summary-warn">
          <span className="cm-summary-count">{warn}</span>
          <span className="cm-summary-label">Segregar</span>
        </div>
        <div className="cm-summary-card cm-summary-bad">
          <span className="cm-summary-count">{bad}</span>
          <span className="cm-summary-label">Incompatibles</span>
        </div>
      </div>

      <div className="cm-table-wrap" ref={tableWrapRef}>
        <table className="cm-table">
          <thead>
            <tr>
              <th className="cm-th-corner"><span className="cm-corner-label">Producto ↓ / →</span></th>
              {products.map(p => (
                <th key={p.id} className="cm-th-col">
                  <div className="cm-col-label" title={p.name}>
                    {p.name}
                    {p.clase8_subcategoria && (
                      <span title={`⚠️ ${getClase8Label(p.clase8_subcategoria)}`} style={{ marginLeft: 4 }}>⚠️</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map(row => (
              <tr key={row.id}>
                <td className="cm-td-row">
                  <span title={row.name}>{row.name}</span>
                  {row.clase8_subcategoria && (
                    <span title={`⚠️ ${getClase8Label(row.clase8_subcategoria)}`} style={{ marginLeft: 4 }}>⚠️</span>
                  )}
                </td>
                {products.map((col, colIdx) => (
                  <Cell
                    key={col.id}
                    level={productCompat(row, col)}
                    rowProduct={row}
                    colProduct={col}
                    colIndex={colIdx}
                    rowId={row.id}
                    colId={col.id}
                    onHover={(r, c) => setHoveredCell(r && c ? { row: r, col: c } : null)}
                    onShowTooltip={showTooltipNow}
                    isRowHighlighted={hoveredCell?.row === row.id}
                    isColHighlighted={hoveredCell?.col === col.id}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tooltipData && createPortal(
        (() => {
          const { rowProduct, colProduct, rect } = tooltipData;
          const margin = 8;
          const estimatedWidth = 340;
          const estimatedHeight = 180;
          const wrapRect = tableWrapRef.current?.getBoundingClientRect();
          const boundLeft = wrapRect ? wrapRect.left : margin;
          const boundRight = wrapRect ? wrapRect.right : window.innerWidth - margin;
          const boundTop = wrapRect ? wrapRect.top : margin;
          const boundBottom = wrapRect ? wrapRect.bottom : window.innerHeight - margin;

          let left = rect.left;
          let top = rect.bottom + 6;

          if (left + estimatedWidth > boundRight) {
            left = rect.right - estimatedWidth;
          }
          if (top + estimatedHeight > boundBottom) {
            top = rect.top - estimatedHeight - 6;
          }
          left = Math.max(boundLeft + margin, Math.min(left, boundRight - estimatedWidth - margin));
          top = Math.max(boundTop + margin, Math.min(top, boundBottom - estimatedHeight - margin));
          return (
            <div
              className="cm-tooltip-portal"
              style={{ position: 'fixed', top, left, zIndex: 9999 }}
              onMouseEnter={cancelTooltipClose}
              onMouseLeave={scheduleTooltipClose}
            >
              {(rowProduct.reactive_hazards || rowProduct.incompatible_materials) && (
                <div className="cm-tooltip-product">
                  <strong>{rowProduct.name}</strong>
                  {rowProduct.reactive_hazards && (
                    <p><span className="cm-tooltip-label">10.3 Reacciones peligrosas:</span> {rowProduct.reactive_hazards}</p>
                  )}
                  {rowProduct.incompatible_materials && (
                    <p><span className="cm-tooltip-label">10.5 Materiales incompatibles:</span> {rowProduct.incompatible_materials}</p>
                  )}
                </div>
              )}
              {(colProduct.reactive_hazards || colProduct.incompatible_materials) && (
                <div className="cm-tooltip-product">
                  <strong>{colProduct.name}</strong>
                  {colProduct.reactive_hazards && (
                    <p><span className="cm-tooltip-label">10.3 Reacciones peligrosas:</span> {colProduct.reactive_hazards}</p>
                  )}
                  {colProduct.incompatible_materials && (
                    <p><span className="cm-tooltip-label">10.5 Materiales incompatibles:</span> {colProduct.incompatible_materials}</p>
                  )}
                </div>
              )}
            </div>
          );
        })(),
        document.body
      )}

      <div className="cm-legend">
        <span className="cm-legend-title">Leyenda</span>
        <div className="cm-legend-items">
          {LEGEND.map(l => (
            <div key={l.level} className="cm-legend-item">
              <span className="cm-legend-dot" style={{ background: l.bg, color: l.color, borderColor: l.color + '60' }}>
                {l.level === 'compatible' ? '✓' : l.level === 'precaucion' ? '!' : '✕'}
              </span>
              <div>
                <span className="cm-legend-name" style={{ color: l.color }}>{l.label}</span>
                <span className="cm-legend-desc">{l.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CompatibilityMatrix() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const effectiveCompanyId = impersonated?.id ?? null;

  const [activeTab,    setActiveTab]    = useState<TabId>('general');
  const [products,     setProducts]     = useState<Product[]>([]);
  const [warehouses,   setWarehouses]   = useState<Warehouse[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [exporting,    setExporting]    = useState(false);

  // Comparar tab
  const [compSearch,   setCompSearch]   = useState('');
  const [selectedIds,  setSelectedIds]  = useState<string[]>([]);

  // Bodega tab
  const [selectedWhId,   setSelectedWhId]   = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [whZones,        setWhZones]        = useState<Zone[]>([]);
  const [whLoading,      setWhLoading]      = useState(false);
  const [zonesError,     setZonesError]     = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: prods, error: e1 }, { data: whs, error: e2 }] = await Promise.all([
      effectiveCompanyId
        ? supabase.from('products').select('id, name, sga_classes, reactive_hazards, incompatible_materials, clase8_subcategoria').eq('company_id', effectiveCompanyId).order('name')
        : supabase.from('products').select('id, name, sga_classes, reactive_hazards, incompatible_materials, clase8_subcategoria').order('name'),
      effectiveCompanyId
        ? supabase.from('warehouses').select('id, name, description').eq('company_id', effectiveCompanyId).order('name')
        : supabase.from('warehouses').select('id, name, description').order('name'),
    ]);
    if (e1 || e2) setError('Error al cargar datos: ' + (e1?.message ?? e2?.message));
    else { setProducts(prods ?? []); setWarehouses(whs ?? []); }
    setLoading(false);
  }, [effectiveCompanyId]);

  const fetchZones = useCallback(async (whId: string) => {
    setWhLoading(true);
    setZonesError('');
    const { data, error: err } = await supabase
      .from('warehouse_zones')
      .select('id, warehouse_id, name, product_ids')
      .eq('warehouse_id', whId);
    if (err) setZonesError('Error al cargar zonas de la bodega. Intenta de nuevo.');
    else setWhZones(data ?? []);
    setWhLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (selectedWhId) fetchZones(selectedWhId);
    else setWhZones([]);
    setSelectedZoneId('');
  }, [selectedWhId, fetchZones]);

  // Derived data
  const whProductIds  = Array.from(new Set(whZones.flatMap(z => z.product_ids ?? [])));
  const whProducts    = products.filter(p => whProductIds.includes(p.id));
  const compProducts  = products.filter(p => selectedIds.includes(p.id));

  const zoneProductIds    = selectedZoneId
    ? (whZones.find(z => z.id === selectedZoneId)?.product_ids ?? [])
    : whProductIds;
  const filteredWhProducts = products.filter(p => zoneProductIds.includes(p.id));

  const whAlerts = filteredWhProducts.length >= 2
    ? (() => {
        const pairs: { a: Product; b: Product; level: CompatLevel }[] = [];
        for (let i = 0; i < filteredWhProducts.length; i++)
          for (let j = i + 1; j < filteredWhProducts.length; j++) {
            const level = productCompat(filteredWhProducts[i], filteredWhProducts[j]);
            if (level !== 'compatible' && level !== 'self')
              pairs.push({ a: filteredWhProducts[i], b: filteredWhProducts[j], level });
          }
        return pairs;
      })()
    : [];

  // ── Loading / error ──

  if (loading) return (
    <div className="cm-center"><span className="cm-spinner" /><p>Cargando datos…</p></div>
  );
  if (error) return (
    <div className="pr-error-box">
      <p>{error}</p>
      <button className="pr-btn-retry" onClick={fetchData}>Reintentar</button>
    </div>
  );

  // ── Render ──

  return (
    <div className="cm-root">

      {/* ── Tab bar ── */}
      <div className="cm-tabs">
        {([
          { id: 'general',  label: 'Matriz General',     icon: <GridIcon /> },
          { id: 'comparar', label: 'Comparar Productos', icon: <CompareIcon /> },
          { id: 'bodega',   label: 'Por Bodega',          icon: <WhIcon /> },
        ] as const).map(({ id, label, icon }) => (
          <button
            key={id}
            className={`cm-tab${activeTab === id ? ' active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════
          Tab: Matriz General
         ════════════════════════════════════════════ */}
      {activeTab === 'general' && (
        <>
          <div className="cm-tab-header">
            <div>
              <h3 className="cm-tab-title">Matriz General</h3>
              <p className="cm-tab-sub">{products.length} producto{products.length !== 1 ? 's' : ''} en el sistema</p>
            </div>
            {products.length >= 2 && (
              <button
                className="cm-btn-export"
                onClick={() => exportToPDF(products, 'Todos los productos', setExporting, displayCompany?.name, displayCompany?.ruc)}
                disabled={exporting}
              >
                {exporting ? <span className="cm-spinner-sm" /> : <PdfIcon />}
                {exporting ? 'Generando…' : 'Exportar PDF'}
              </button>
            )}
          </div>

          {products.length < 2 ? (
            <div className="cm-center">
              <EmptyIcon />
              <p className="cm-empty-title">
                {products.length === 0 ? 'Sin productos' : 'Se necesitan al menos 2 productos'}
              </p>
              <p className="cm-empty-sub">
                {products.length === 0
                  ? 'Agrega productos en el módulo de Productos para ver la matriz.'
                  : 'Agrega más productos para generar la matriz de compatibilidad.'}
              </p>
            </div>
          ) : (
            <MatrixPanel products={products} />
          )}
        </>
      )}

      {/* ════════════════════════════════════════════
          Tab: Comparar Productos
         ════════════════════════════════════════════ */}
      {activeTab === 'comparar' && (
        <div className="cm-comparar-layout">

          {/* Left: selector */}
          <div className="cm-selector-panel">
            <div className="cm-selector-header">
              <span className="cm-selector-title">Seleccionar productos</span>
              {selectedIds.length > 0 && (
                <button className="cm-btn-clear" onClick={() => setSelectedIds([])}>Limpiar</button>
              )}
            </div>
            <input
              className="cm-search"
              type="text"
              placeholder="Buscar producto…"
              value={compSearch}
              onChange={e => setCompSearch(e.target.value)}
            />
            <div className="cm-selector-list">
              {products
                .filter(p => p.name.toLowerCase().includes(compSearch.toLowerCase()))
                .map(p => {
                  const checked = selectedIds.includes(p.id);
                  return (
                    <label key={p.id} className={`cm-selector-item${checked ? ' checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedIds(prev =>
                            checked ? prev.filter(id => id !== p.id) : [...prev, p.id]
                          )
                        }
                      />
                      <span className="cm-selector-name">{p.name}</span>
                      {p.sga_classes.length > 0 && (
                        <span className="cm-sga-badge">
                          {p.sga_classes.map(c => c.replace('clase', 'C')).join(', ')}
                        </span>
                      )}
                    </label>
                  );
                })}
              {products.length === 0 && (
                <p className="cm-selector-empty">Sin productos registrados.</p>
              )}
            </div>
            <div className="cm-selector-footer">
              {selectedIds.length === 0
                ? 'Selecciona al menos 2 productos'
                : selectedIds.length === 1
                ? '1 seleccionado — agrega uno más'
                : `${selectedIds.length} productos seleccionados`}
            </div>
          </div>

          {/* Right: result */}
          <div className="cm-comparar-result">
            {compProducts.length < 2 ? (
              <div className="cm-center">
                <CompareLgIcon />
                <p className="cm-empty-title">Selecciona 2 o más productos</p>
                <p className="cm-empty-sub">Usa el panel izquierdo para elegir los productos que deseas comparar antes de almacenarlos juntos.</p>
              </div>
            ) : (
              <>
                <div className="cm-tab-header">
                  <div>
                    <h3 className="cm-tab-title">Comparando {compProducts.length} productos</h3>
                    <p className="cm-tab-sub">Compatibilidad según la NTE INEN 2266:2013</p>
                  </div>
                  <button
                    className="cm-btn-export"
                    onClick={() => exportToPDF(compProducts, `${compProducts.length} productos seleccionados`, setExporting, displayCompany?.name, displayCompany?.ruc)}
                    disabled={exporting}
                  >
                    {exporting ? <span className="cm-spinner-sm" /> : <PdfIcon />}
                    {exporting ? 'Generando…' : 'Exportar PDF'}
                  </button>
                </div>

                <div className="cm-pairs-grid">
                  {compProducts.flatMap((a, i) =>
                    compProducts.slice(i + 1).map(b => {
                      const lv = productCompat(a, b);
                      const cfg = {
                        compatible:   { icon: '✓', cls: 'ok',   label: 'Compatible',   desc: 'Pueden almacenarse juntos sin restricciones.' },
                        precaucion:   { icon: '!', cls: 'warn', label: 'Precaución',    desc: 'Separar físicamente según la ficha de datos de seguridad (SDS).' },
                        incompatible: { icon: '✕', cls: 'bad',  label: 'Incompatible',  desc: 'No almacenar juntos — riesgo grave de reacción.' },
                        self:         { icon: '—', cls: '',     label: '',              desc: '' },
                      }[lv];
                      return (
                        <div key={`${a.id}-${b.id}`} className={`cm-pair-card cm-pair-${cfg.cls}`}>
                          <span className={`cm-pair-badge cm-pair-badge-${cfg.cls}`}>{cfg.icon}</span>
                          <div className="cm-pair-info">
                            <div className="cm-pair-names">
                              <span className="cm-pair-name">{a.name}</span>
                              <span className="cm-pair-sep">+</span>
                              <span className="cm-pair-name">{b.name}</span>
                            </div>
                            <span className={`cm-pair-level cm-pair-level-${cfg.cls}`}>{cfg.label}</span>
                            <p className="cm-pair-desc">{cfg.desc}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {compProducts.length > 2 && (
                  <>
                    <div className="cm-section-divider">Matriz completa</div>
                    <MatrixPanel products={compProducts} />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════
          Tab: Por Bodega
         ════════════════════════════════════════════ */}
      {activeTab === 'bodega' && (
        <>
          <div className="cm-tab-header">
            <div>
              <h3 className="cm-tab-title">Compatibilidad por Bodega</h3>
              <p className="cm-tab-sub">Productos asignados en las zonas de la bodega seleccionada</p>
            </div>
            {filteredWhProducts.length >= 2 && (
              <button
                className="cm-btn-export"
                onClick={() => {
                  const wh   = warehouses.find(w => w.id === selectedWhId);
                  const zone = whZones.find(z => z.id === selectedZoneId);
                  const subtitle = zone
                    ? `Bodega: ${wh?.name ?? ''} · Zona: ${zone.name}`
                    : `Bodega: ${wh?.name ?? ''}`;
                  exportToPDF(filteredWhProducts, subtitle, setExporting, displayCompany?.name, displayCompany?.ruc);
                }}
                disabled={exporting}
              >
                {exporting ? <span className="cm-spinner-sm" /> : <PdfIcon />}
                {exporting ? 'Generando…' : 'Exportar PDF'}
              </button>
            )}
          </div>

          <SelectBuscable
            className="cm-wh-buscable"
            inputStyle={CM_WH_INPUT}
            ariaLabel="Bodega"
            value={selectedWhId}
            onChange={setSelectedWhId}
            vacio="— Selecciona una bodega —"
            opciones={warehouses.map(wh => ({ value: wh.id, label: wh.name }))}
          />

          {selectedWhId && !whLoading && whZones.length > 0 && (
            <SelectBuscable
              className="cm-wh-buscable"
              inputStyle={CM_WH_INPUT}
              ariaLabel="Zona"
              value={selectedZoneId}
              onChange={setSelectedZoneId}
              vacio={`— Todas las zonas (${whProducts.length} prod.) —`}
              opciones={whZones.map(z => ({ value: z.id, label: `${z.name} (${(z.product_ids ?? []).length} prod.)` }))}
            />
          )}

          {!selectedWhId ? (
            <div className="cm-center">
              <WhLgIcon />
              <p className="cm-empty-title">Selecciona una bodega</p>
              <p className="cm-empty-sub">Elige una bodega del menú superior para ver la matriz de compatibilidad de sus productos.</p>
            </div>
          ) : whLoading ? (
            <div className="cm-center"><span className="cm-spinner" /><p>Cargando productos…</p></div>
          ) : zonesError ? (
            <div className="pr-error-box">
              <p>{zonesError}</p>
              <button className="pr-btn-retry" onClick={() => fetchZones(selectedWhId)}>Reintentar</button>
            </div>
          ) : filteredWhProducts.length < 2 ? (
            <div className="cm-center">
              <EmptyIcon />
              <p className="cm-empty-title">
                {filteredWhProducts.length === 0
                  ? (selectedZoneId ? 'Sin productos en esta zona' : 'Sin productos asignados')
                  : (selectedZoneId ? 'Solo 1 producto en esta zona' : 'Solo 1 producto en esta bodega')}
              </p>
              <p className="cm-empty-sub">
                {filteredWhProducts.length === 0
                  ? (selectedZoneId
                      ? 'Esta zona no tiene productos asignados.'
                      : 'Esta bodega no tiene productos asignados en sus zonas.')
                  : 'Se necesitan al menos 2 productos para generar la matriz.'}
              </p>
            </div>
          ) : (
            <>
              {whAlerts.length > 0 ? (
                <div className="cm-wh-alerts">
                  <div className="cm-wh-alerts-title">
                    <AlertTriIcon />
                    {whAlerts.some(p => p.level === 'incompatible')
                      ? plural(whAlerts.filter(p => p.level === 'incompatible').length, 'incompatibilidad crítica detectada', 'incompatibilidades críticas detectadas')
                      : plural(whAlerts.length, 'restricción de segregación detectada', 'restricciones de segregación detectadas')}
                  </div>
                  <div className="cm-wh-alerts-list">
                    {whAlerts.map(({ a, b, level }) => (
                      <div key={`${a.id}-${b.id}`} className={`cm-wh-alert-item cm-wh-alert-${level}`}>
                        <span className="cm-wh-alert-icon">{level === 'incompatible' ? '✕' : '!'}</span>
                        <span>
                          <strong>{a.name}</strong>
                          {level === 'incompatible' ? ' es incompatible con ' : ' requiere precaución con '}
                          <strong>{b.name}</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="cm-wh-all-ok">
                  <CheckCircleIcon />
                  <span>Todos los productos de esta bodega son compatibles entre sí.</span>
                </div>
              )}
              <MatrixPanel products={filteredWhProducts} />
            </>
          )}
        </>
      )}

    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function EmptyIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: '#334155' }}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function CompareIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function CompareLgIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: '#334155' }}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function WhIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function WhLgIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: '#334155' }}>
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}

function AlertTriIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
